import { useEffect, useMemo, useRef, useState } from "react";

import useColorPalettesImportFilename from "components/live-event/use-color-palettes-import-filename";
import { useTemplateFilename } from "hooks/template-name.hooks";
import { useTypedSelector } from "store/reducers/use-typed-selector";
import { TemplateClassNames } from "types/template-layouts";

const useLoadTemplateStyles = () => {
	const retryLoadTemplateTimeout = useRef<NodeJS.Timeout | null>(null);

	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);

	const [retryTemplateLoaded, setRetryTemplateLoaded] = useState(0);
	const [hasLoadedStyles, setHasLoadedStyles] = useState(false);
	const [templateLoaded, setTemplateLoaded] = useState(false);

	const templateFileName = useTemplateFilename(workingEvent);
	const importFilename = useColorPalettesImportFilename({
		filename: templateFileName,
	});
	const workingEventTemplateName = useMemo(() => {
		if (workingEvent?.template?.name) {
			return workingEvent.template.name;
		}
		return null;
	}, [workingEvent?.template?.name]);

	const hasWorkingEventTemplate = useMemo(() => {
		return !!workingEvent?.template;
	}, [workingEvent?.template]);
	const hasWorkingEvent = useMemo(() => {
		return !!workingEvent;
	}, [workingEvent]);

	useEffect(() => {
		// async import live event template styling
		async function loadTemplate() {
			if (importFilename && !hasLoadedStyles) {
				setHasLoadedStyles(true);
				await import(`../../../scss/live-event/${importFilename}`);
			}
			setTemplateLoaded(true);
		}

		if (workingEventTemplateName && TemplateClassNames[workingEventTemplateName]) {
			// only load if we have a filename and we have pulled the flags, because if we try and render before we know 
			// the state of the feature flag, we will compile the wrong file
			if (importFilename) {
				loadTemplate();
			}
		} else {
			if (document.querySelector('#custom-template-styling')) {
				setTemplateLoaded(true);
			} else {

				// if the previously selected theme was deleted, we need to force the user to select a new theme
				if (hasWorkingEvent && !hasWorkingEventTemplate) {
					return;
				}

				retryLoadTemplateTimeout.current = setTimeout(() => {
					setRetryTemplateLoaded(prev => prev + 1);
				}, 1000);
			}
		}

		return () => {
			if (retryLoadTemplateTimeout.current) {
				clearTimeout(retryLoadTemplateTimeout.current);
			}
		};
	}, [
		hasWorkingEventTemplate,
		hasWorkingEvent,
		importFilename,
		workingEventTemplateName,
		hasLoadedStyles,
		retryTemplateLoaded,
	]);
};

export default useLoadTemplateStyles;
